import { useEffect, useMemo, useState } from 'react';
import {
  alpha,
  Box,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { motion } from 'framer-motion';
import {
  getIndustrialCostCurrentCostResults,
  getIndustrialCostDirectCosts,
  getIndustrialCostEconomicMovements,
  getIndustrialCostMovementClassifications,
  getIndustrialCostProducts,
  getIndustrialCostStages,
} from '../services/industrialCostService';
import type {
  CurrentCostResult,
  DirectCost,
  EconomicMovement,
  MovementClassification,
  Product,
  Stage,
} from '../types/industrialCost';

import { esNumberFormat } from '../utils/numberFormat';
const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, ease: 'easeOut' },
  },
};

type SummaryCardProps = {
  eyebrow: string;
  title: string;
  value: string;
  helper: string;
  icon: React.ReactNode;
  accentColor: string;
  delay?: number;
};

type CostPayloadView = {
  directMaterialCostTotal: number;
  laborCostTotal: number;
  costStatus: string | null;
  costStatusLabel: string | null;
  costWarningsCount: number;
  unclassifiedDirectCost: number;
};

type ActionItem = {
  title: string;
  description: string;
  tone: 'success' | 'warning' | 'error' | 'info';
};

function formatCurrency(value: number | null | undefined, maximumFractionDigits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }

  return esNumberFormat({
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits,
  }).format(value);
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }

  return `${esNumberFormat({
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value * 100)} %`;
}

const MONTH_OPTIONS = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

function formatCurrentPeriod(year: number, month: number): string {
  return `${MONTH_OPTIONS[month - 1] ?? `Mes ${month}`} ${year}`;
}

function getResultPayload(item: CurrentCostResult): CostPayloadView {
  const payload =
    typeof item.result_payload === 'object' && item.result_payload !== null
      ? (item.result_payload as Record<string, unknown>)
      : {};

  const directBreakdown =
    typeof payload.direct_cost_breakdown === 'object' &&
    payload.direct_cost_breakdown !== null &&
    !Array.isArray(payload.direct_cost_breakdown)
      ? (payload.direct_cost_breakdown as Record<string, unknown>)
      : {};

  const warnings = Array.isArray(payload.cost_warnings) ? payload.cost_warnings : [];

  return {
    directMaterialCostTotal:
      typeof payload.direct_material_cost_total === 'number'
        ? payload.direct_material_cost_total
        : 0,
    laborCostTotal:
      typeof payload.labor_cost_total === 'number'
        ? payload.labor_cost_total
        : 0,
    costStatus: typeof payload.cost_status === 'string' ? payload.cost_status : null,
    costStatusLabel:
      typeof payload.cost_status_label === 'string' ? payload.cost_status_label : null,
    costWarningsCount: warnings.length,
    unclassifiedDirectCost:
      typeof directBreakdown.__unclassified__ === 'number' ? directBreakdown.__unclassified__ : 0,
  };
}

function SummaryCard({
  eyebrow,
  title,
  value,
  helper,
  icon,
  accentColor,
  delay = 0,
}: SummaryCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, delay, ease: 'easeOut' }}
      style={{ height: '100%' }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: 'rgba(15,23,42,0.62)',
          backdropFilter: 'blur(14px)',
          border: `1px solid ${alpha(accentColor, 0.18)}`,
          borderRadius: 4,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            right: -10,
            bottom: -14,
            color: alpha(accentColor, 0.11),
            transform: 'rotate(-10deg)',
            '& svg': { fontSize: 104 },
          }}
        >
          {icon}
        </Box>

        <Stack spacing={1.1} sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="overline"
            sx={{ color: accentColor, fontWeight: 900, letterSpacing: 1.2, lineHeight: 1 }}
          >
            {eyebrow}
          </Typography>

          <Typography variant="body2" sx={{ color: '#cbd5e1', fontWeight: 800 }}>
            {title}
          </Typography>

          <Typography
            variant="h4"
            sx={{
              color: '#f8fafc',
              fontWeight: 950,
              lineHeight: 1,
              letterSpacing: '-0.8px',
            }}
          >
            {value}
          </Typography>

          <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.65 }}>
            {helper}
          </Typography>
        </Stack>
      </Paper>
    </motion.div>
  );
}

export default function IndustrialCostSummaryView() {
  const [stages, setStages] = useState<Stage[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [movements, setMovements] = useState<EconomicMovement[]>([]);
  const [classifications, setClassifications] = useState<MovementClassification[]>([]);
  const [directCosts, setDirectCosts] = useState<DirectCost[]>([]);
  const [costResults, setCostResults] = useState<CurrentCostResult[]>([]);
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth() + 1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadOverview() {
      setIsLoading(true);
      setLoadError(null);

      try {
        const [
          stagesResponse,
          productsResponse,
          movementsResponse,
          classificationsResponse,
          directCostsResponse,
        ] = await Promise.all([
          getIndustrialCostStages(),
          getIndustrialCostProducts(),
          getIndustrialCostEconomicMovements(),
          getIndustrialCostMovementClassifications(),
          getIndustrialCostDirectCosts(),
        ]);

        if (!isMounted) return;

        // Detectar automáticamente el último periodo con datos reales
        let resolvedYear = new Date().getFullYear();
        let resolvedMonth = new Date().getMonth() + 1;

        if (movementsResponse.length > 0) {
          const sorted = [...movementsResponse].sort((a, b) => {
            if (a.period_year !== b.period_year) return b.period_year - a.period_year;
            const aMonth = a.period_month ?? 12;
            const bMonth = b.period_month ?? 12;
            return bMonth - aMonth;
          });
          const latest = sorted[0];
          resolvedYear = latest.period_year;
          resolvedMonth = latest.period_month ?? 12;
        }

        const costResultsResponse = await getIndustrialCostCurrentCostResults(
          resolvedYear,
          resolvedMonth,
        );

        if (!isMounted) return;

        setStages(stagesResponse);
        setProducts(productsResponse);
        setMovements(movementsResponse);
        setClassifications(classificationsResponse);
        setDirectCosts(directCostsResponse);
        setCostResults(costResultsResponse);
        setCurrentYear(resolvedYear);
        setCurrentMonth(resolvedMonth);
      } catch (error) {
        if (!isMounted) return;
        setLoadError('No se pudo cargar el resumen operativo del sistema.');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadOverview();

    return () => {
      isMounted = false;
    };
  }, []);

  const summary = useMemo(() => {
    const activeProducts = products.filter((item) => item.is_active);
    const activeStages = stages.filter((item) => item.is_active);
    const primaryStages = activeStages.filter((item) => item.stage_type === 'primary');
    const auxiliaryStages = activeStages.filter((item) => item.stage_type === 'auxiliary');

    const classifiedMovements = classifications.filter(
      (item) => item.classification_status === 'classified',
    );

    const indirectClassifications = classifiedMovements.filter(
      (item) => item.economic_type === 'cost' && item.cost_type === 'indirect',
    );

    const directClassifications = classifiedMovements.filter(
      (item) => item.economic_type === 'cost' && item.cost_type === 'direct',
    );

    const payloads = costResults.map(getResultPayload);
    const productsWithCost = costResults.filter((item) => item.total_cost > 0);
    const totalCost = costResults.reduce((acc, item) => acc + item.total_cost, 0);
    const totalDirectCost = costResults.reduce((acc, item) => acc + item.direct_cost_total, 0);
    const totalIndirectCost = costResults.reduce((acc, item) => acc + item.indirect_cost_total, 0);
    const totalMaterialCost = payloads.reduce((acc, item) => acc + item.directMaterialCostTotal, 0);
    const totalLaborCost = payloads.reduce((acc, item) => acc + item.laborCostTotal, 0);
    const totalUnclassifiedCost = payloads.reduce((acc, item) => acc + item.unclassifiedDirectCost, 0);
    const totalWarnings = payloads.reduce((acc, item) => acc + item.costWarningsCount, 0);

    const completeResults = payloads.filter((item) => item.costStatus === 'complete').length;
    const warningResults = payloads.filter((item) => item.costStatus === 'warning').length;
    const partialResults = payloads.filter((item) => item.costStatus === 'partial').length;
    const missingResults = payloads.filter((item) => item.costStatus === 'missing').length;

    const totalUnits = costResults.reduce((acc, item) => {
      const payload =
        typeof item.result_payload === 'object' && item.result_payload !== null
          ? (item.result_payload as Record<string, unknown>)
          : {};
      const units = typeof payload.units_produced === 'number' ? payload.units_produced : 0;
      return acc + units;
    }, 0);

    const weightedUnitCost = totalUnits > 0 ? totalCost / totalUnits : null;

    const directCostProducts = new Set(directCosts.map((item) => item.product_id)).size;
    const indirectMovementIds = new Set(indirectClassifications.map((item) => item.movement_id));
    const directCostCategories = new Set(
      directCosts.map((item) => item.cost_category).filter(Boolean),
    ).size;

    const readinessChecks = [
      activeProducts.length > 0,
      activeStages.length > 0,
      primaryStages.length > 0,
      costResults.length > 0,
      productsWithCost.length > 0,
      totalMaterialCost > 0,
      totalLaborCost > 0,
    ];
    const readinessScore =
      readinessChecks.filter(Boolean).length / Math.max(readinessChecks.length, 1);

    const actionItems: ActionItem[] = [];

    if (activeProducts.length === 0) {
      actionItems.push({
        tone: 'error',
        title: 'Cargar productos',
        description: 'Sin productos activos no existe unidad final sobre la que reconstruir coste.',
      });
    }

    if (activeStages.length === 0 || primaryStages.length === 0) {
      actionItems.push({
        tone: 'error',
        title: 'Completar estructura productiva',
        description: 'Las etapas principales son necesarias para explicar repartos e imputación industrial.',
      });
    }

    if (costResults.length === 0) {
      actionItems.push({
        tone: 'warning',
        title: 'Sin resultados para el mes actual',
        description: 'No hay productos con coste calculable para el periodo mostrado en esta home.',
      });
    }

    if (costResults.length > 0 && totalIndirectCost <= 0) {
      actionItems.push({
        tone: 'warning',
        title: 'Indirectos no aplicados',
        description: 'Los resultados actuales no tienen coste indirecto. Revisa si este mes debería absorber estructura indirecta.',
      });
    }

    if (totalUnclassifiedCost > 0) {
      actionItems.push({
        tone: 'warning',
        title: 'Coste directo sin clasificar',
        description: 'Hay coste conservado en “Sin clasificar”. El importe existe, pero falta precisión analítica.',
      });
    }

    if (totalWarnings > 0) {
      actionItems.push({
        tone: 'warning',
        title: 'Resultados con avisos',
        description: 'La pantalla de Resultados contiene advertencias que afectan a la lectura del coste por producto.',
      });
    }

    if (actionItems.length === 0) {
      actionItems.push({
        tone: 'success',
        title: 'Sistema preparado para lectura de coste',
        description: 'El periodo actual tiene base suficiente para revisar coste industrial por producto.',
      });
    }

    return {
      activeProducts,
      activeStages,
      primaryStages,
      auxiliaryStages,
      classifiedMovements,
      indirectClassifications,
      directClassifications,
      directCostProducts,
      indirectMovementIds,
      directCostCategories,
      productsWithCost,
      totalCost,
      totalDirectCost,
      totalIndirectCost,
      totalMaterialCost,
      totalLaborCost,
      totalUnclassifiedCost,
      totalWarnings,
      completeResults,
      warningResults,
      partialResults,
      missingResults,
      weightedUnitCost,
      readinessScore,
      actionItems,
    };
  }, [classifications, costResults, directCosts, products, stages]);

  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${alpha('#94a3b8', 0.16)}`,
          bgcolor: 'rgba(8,18,36,0.88)',
          backdropFilter: 'blur(10px)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 4, md: 5 },
        }}
      >
        <Stack spacing={2} alignItems="center" justifyContent="center">
          <CircularProgress sx={{ color: '#38bdf8' }} />
          <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 600 }}>
            Cargando resumen operativo...
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (loadError) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${alpha('#ef4444', 0.2)}`,
          bgcolor: 'rgba(8,18,36,0.88)',
          backdropFilter: 'blur(10px)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 3, md: 3.5 },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <WarningAmberRoundedIcon sx={{ color: '#f87171' }} />
          <Typography variant="body2" sx={{ color: '#fecaca', fontWeight: 700 }}>
            {loadError}
          </Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <Stack spacing={0.5}>
      <motion.div initial="hidden" animate="visible" variants={itemVariants}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${alpha('#38bdf8', 0.16)}`,
            bgcolor: 'rgba(8,18,36,0.88)',
            backdropFilter: 'blur(10px)',
            px: { xs: 2.5, md: 3.25 },
            py: { xs: 2.5, md: 3 },
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} lg={8}>
              <Stack spacing={1.45}>
                <Typography
                  variant="overline"
                  sx={{ color: '#7dd3fc', fontWeight: 900, letterSpacing: 1.2 }}
                >
                  Resumen operativo
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 950,
                    lineHeight: 1.08,
                    letterSpacing: '-0.9px',
                    fontSize: { xs: '1.75rem', md: '2.25rem' },
                    maxWidth: 960,
                  }}
                >
                  Estado actual de PROA Costing para reconstruir coste industrial.
                </Typography>

                <Typography variant="body1" sx={{ color: '#cbd5e1', lineHeight: 1.8, maxWidth: 920 }}>
                  Esta pantalla resume si el sistema tiene estructura, datos operativos y resultados
                  suficientes para explicar el coste de producción por producto y mes.
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha('#22c55e', 0.18)}`,
                  bgcolor: alpha('#22c55e', 0.055),
                  p: 2,
                }}
              >
                <Stack spacing={1.2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" sx={{ color: '#86efac', fontWeight: 900 }}>
                        Preparación del sistema
                      </Typography>
                      <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 950, mt: 0.4 }}>
                        {formatPercent(summary.readinessScore)}
                      </Typography>
                    </Box>
                    <Chip
                      label={formatCurrentPeriod(currentYear, currentMonth)}
                      size="small"
                      sx={{
                        color: '#bae6fd',
                        bgcolor: alpha('#38bdf8', 0.12),
                        border: `1px solid ${alpha('#38bdf8', 0.22)}`,
                        fontWeight: 800,
                      }}
                    />
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={summary.readinessScore * 100}
                    sx={{
                      height: 8,
                      borderRadius: 999,
                      bgcolor: alpha('#94a3b8', 0.16),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 999,
                        bgcolor: '#22c55e',
                      },
                    }}
                  />

                  <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                    Indicador orientativo basado en maestros, estructura, resultados y capas de coste
                    disponibles para el mes actual.
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} xl={3}>
          <SummaryCard
            eyebrow="Resultado mensual"
            title="Coste total visible"
            value={formatCurrency(summary.totalCost)}
            helper={`${summary.productsWithCost.length} producto(s) con coste en ${formatCurrentPeriod(currentYear, currentMonth)}.`}
            icon={<AssessmentOutlinedIcon />}
            accentColor="#38bdf8"
            delay={0.02}
          />
        </Grid>

        <Grid item xs={12} sm={6} xl={3}>
          <SummaryCard
            eyebrow="Coste unitario"
            title="Promedio ponderado"
            value={formatCurrency(summary.weightedUnitCost, 4)}
            helper="Calculado sobre unidades producidas disponibles en el periodo actual."
            icon={<PrecisionManufacturingOutlinedIcon />}
            accentColor="#7dd3fc"
            delay={0.06}
          />
        </Grid>

        <Grid item xs={12} sm={6} xl={3}>
          <SummaryCard
            eyebrow="Estructura"
            title="Productos activos"
            value={String(summary.activeProducts.length)}
            helper={`${summary.activeStages.length} etapas activas para modelizar el proceso productivo.`}
            icon={<Inventory2OutlinedIcon />}
            accentColor="#a78bfa"
            delay={0.1}
          />
        </Grid>

        <Grid item xs={12} sm={6} xl={3}>
          <SummaryCard
            eyebrow="Calidad del coste"
            title="Avisos en resultados"
            value={String(summary.totalWarnings)}
            helper="Advertencias que afectan a la lectura del coste por producto."
            icon={<WarningAmberRoundedIcon />}
            accentColor={summary.totalWarnings > 0 ? '#f59e0b' : '#22c55e'}
            delay={0.14}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <Paper
            elevation={0}
            sx={{
              height: '100%',
              borderRadius: 4,
              border: `1px solid ${alpha('#38bdf8', 0.14)}`,
              bgcolor: 'rgba(8,18,36,0.86)',
              backdropFilter: 'blur(12px)',
              px: 2.75,
              py: 2.75,
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" sx={{ color: '#7dd3fc', fontWeight: 900, letterSpacing: 1.2, lineHeight: 1 }}>
                  Coste por producto
                </Typography>
                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 950, mt: 0.8 }}>
                  Resumen de {formatCurrentPeriod(currentYear, currentMonth)}
                </Typography>
              </Box>

              <Box>
                <Stack direction="row" sx={{ borderBottom: '1px solid rgba(148,163,184,0.12)', pb: 0.75, mb: 0.5 }}>
                  <Typography sx={{ flex: 1, color: '#64748b', fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Producto</Typography>
                  <Typography sx={{ width: 140, textAlign: 'right', color: '#64748b', fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Coste total</Typography>
                  <Typography sx={{ width: 120, textAlign: 'right', color: '#64748b', fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Directo</Typography>
                  <Typography sx={{ width: 120, textAlign: 'right', color: '#64748b', fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Indirecto</Typography>
                  <Typography sx={{ width: 100, textAlign: 'right', color: '#7dd3fc', fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>€/ud</Typography>
                </Stack>

                {[...summary.productsWithCost]
                  .sort((a, b) => b.total_cost - a.total_cost)
                  .slice(0, 10)
                  .map((result) => {
                  const payload = typeof result.result_payload === 'object' && result.result_payload !== null
                    ? result.result_payload as Record<string, unknown>
                    : {};
                  const directCost = typeof payload.direct_material_cost_total === 'number' ? payload.direct_material_cost_total : 0;
                  const unitCost = typeof payload.production_unit_cost === 'number' ? payload.production_unit_cost : null;
                  const productName = products.find(p => p.id === result.product_id)?.name ?? result.product_id;

                  return (
                    <Stack
                      key={result.product_id}
                      direction="row"
                      alignItems="center"
                      sx={{
                        py: 0.9,
                        borderBottom: '1px solid rgba(148,163,184,0.06)',
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      <Typography sx={{ flex: 1, color: '#f8fafc', fontWeight: 700, fontSize: '0.875rem' }} noWrap>
                        {productName}
                      </Typography>
                      <Typography sx={{ width: 140, textAlign: 'right', color: '#cbd5e1', fontWeight: 700, fontSize: '0.875rem' }}>
                        {formatCurrency(result.total_cost)}
                      </Typography>
                      <Typography sx={{ width: 120, textAlign: 'right', color: '#86efac', fontWeight: 700, fontSize: '0.875rem' }}>
                        {formatCurrency(directCost)}
                      </Typography>
                      <Typography sx={{ width: 120, textAlign: 'right', color: '#7dd3fc', fontWeight: 700, fontSize: '0.875rem' }}>
                        {formatCurrency(result.indirect_cost_total)}
                      </Typography>
                      <Typography sx={{ width: 100, textAlign: 'right', color: '#7dd3fc', fontWeight: 900, fontSize: '0.875rem' }}>
                        {unitCost !== null ? `${unitCost.toFixed(4)} €` : '—'}
                      </Typography>
                    </Stack>
                  );
                })}
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack spacing={2.5} sx={{ height: '100%' }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: `1px solid ${alpha('#38bdf8', 0.14)}`,
                bgcolor: 'rgba(8,18,36,0.86)',
                backdropFilter: 'blur(12px)',
                px: 2.75,
                py: 2.75,
              }}
            >
              <Stack spacing={1.5}>
                <Typography variant="overline" sx={{ color: '#7dd3fc', fontWeight: 900, letterSpacing: 1.2, lineHeight: 1 }}>
                  Composición del coste
                </Typography>
                {[
                  { label: 'Material directo', value: summary.totalMaterialCost, color: '#22c55e' },
                  { label: 'Mano de obra', value: summary.totalLaborCost, color: '#f59e0b' },
                  { label: 'Indirectos', value: summary.totalIndirectCost, color: '#38bdf8' },
                ].map(({ label, value, color }) => (
                  <Box key={label}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                      <Typography sx={{ color: '#cbd5e1', fontWeight: 700, fontSize: '0.84rem' }}>{label}</Typography>
                      <Typography sx={{ color: '#f8fafc', fontWeight: 800, fontSize: '0.84rem' }}>
                        {formatPercent(summary.totalCost > 0 ? value / summary.totalCost : null)}
                      </Typography>
                    </Stack>
                    <Box sx={{ height: 6, borderRadius: 999, bgcolor: 'rgba(148,163,184,0.12)', overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', borderRadius: 999, bgcolor: color, width: `${summary.totalCost > 0 ? (value / summary.totalCost) * 100 : 0}%`, transition: 'width 0.6s ease' }} />
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: `1px solid ${alpha('#a78bfa', 0.14)}`,
                bgcolor: 'rgba(8,18,36,0.86)',
                backdropFilter: 'blur(12px)',
                px: 2.75,
                py: 2.75,
                flex: 1,
              }}
            >
              <Stack spacing={1.5}>
                <Typography variant="overline" sx={{ color: '#c4b5fd', fontWeight: 900, letterSpacing: 1.2, lineHeight: 1 }}>
                  Estructura
                </Typography>
                {[
                  { label: 'Etapas principales', value: String(summary.primaryStages.length), color: '#a78bfa' },
                  { label: 'Etapas auxiliares', value: String(summary.auxiliaryStages.length), color: '#c084fc' },
                  { label: 'Movimientos clasificados', value: `${summary.classifiedMovements.length} / ${movements.length}`, color: '#38bdf8' },
                  { label: 'Productos con directos', value: String(summary.directCostProducts), color: '#f59e0b' },
                ].map(({ label, value, color }) => (
                  <Stack key={label} direction="row" justifyContent="space-between" alignItems="center"
                    sx={{ borderBottom: '1px solid rgba(148,163,184,0.07)', pb: 1, '&:last-child': { borderBottom: 'none', pb: 0 } }}
                  >
                    <Typography sx={{ color: '#94a3b8', fontWeight: 700, fontSize: '0.84rem' }}>{label}</Typography>
                    <Typography sx={{ color, fontWeight: 900, fontSize: '0.875rem' }}>{value}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}
